import { Link } from "react-router";
import type { BookDayView } from "@/design/BookAppointment";
import {
  BusinessBookings,
  type BusinessBookingView,
} from "@/design/BusinessBookings";
import { DataUnavailable } from "@/design/DataUnavailable";
import { PageShell } from "@/design/ui";
import { businessCopyFor } from "@/modules/booking/commands/businessCopy";
import { StatusGuide } from "@/design/BookingStatusBadge";
import { BusinessPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { apiFormAction } from "~/screens/business/SettingsToolScreens";
import { useQuery } from "~/lib/useQuery";

/**
 * BusinessBookingsScreen (docs/ux/screens/BusinessBookingsScreen.md), the
 * clinic's queue: approve, decline, suggest another time, cancel.
 *
 * **The whole screen re-reads after any decision**, rather than patching the
 * row that changed. That is deliberate on this screen above all others: a
 * decision moves a booking between states, can free or take a Time Block that
 * another row offers as a move target, and changes what the *next* decision may
 * be. Patching one row would leave the rest of the queue describing a world
 * that no longer exists, which on a screen whose whole purpose is deciding is
 * the one failure worth spending a round trip to avoid.
 *
 * `canAct` comes from the server (H26). A practitioner-tier member reads this
 * queue and acts on nothing, and every command re-asks through
 * `requireApiManager` regardless.
 */
interface QueuePayload {
  bookings: BusinessBookingView[];
  moveTargets: Record<string, BookDayView[]>;
  assignable: { id: string; displayName: string }[];
  canAct: boolean;
  viewerPractitionerId: string | null;
}

export function BusinessBookingsScreen() {
  const queue = useQuery<QueuePayload>("/business/bookings");
  const reload = () => queue.reload();

  const decision = (path: (form: FormData) => string) =>
    apiFormAction({
      method: "POST",
      path,
      // Booking owns these refusals, so Booking's words say them.
      render: (failure) => ({ formError: businessCopyFor(failure) }),
      onSuccess: reload,
    });

  const bookingId = (form: FormData) => String(form.get("bookingId") ?? "");

  return (
    <BusinessPage>
      <PageShell>
        <div className="mx-auto w-full max-w-md">
          <Link
            to="/business"
            className="mb-4 inline-block text-sm font-medium text-accent"
          >
            ← Back to your business
          </Link>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-xl font-bold text-ink">Bookings</h1>
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-sm">
                <span
                  aria-current="true"
                  className="rounded-md bg-accent px-3 py-1.5 font-medium text-on-accent"
                >
                  List
                </span>
                <Link
                  to="/business/calendar"
                  className="rounded-md px-3 py-1.5 font-medium text-muted hover:text-ink"
                >
                  Week
                </Link>
              </div>
              <StatusGuide />
            </div>
          </div>

          {queue.data && !queue.data.canAct && (
            <p className="mb-4 text-sm text-muted">
              You have view-only access to bookings.
            </p>
          )}

          {queue.loading && !queue.data ? (
            <ScreenPending />
          ) : queue.failure || !queue.data ? (
            <DataUnavailable
              configured={queue.failure !== "not_configured"}
              what="your requests"
            />
          ) : (
            <BusinessBookings
              bookings={queue.data.bookings}
              moveTargets={queue.data.moveTargets}
              assignable={queue.data.assignable}
              canAct={queue.data.canAct}
              viewerPractitionerId={queue.data.viewerPractitionerId}
              commands={{
                // One path per intent, dispatched on the submitting button's
                // own value exactly as the shared form expects.
                decide: decision((form) => {
                  const intent = String(form.get("intent"));
                  const verb =
                    intent === "approve"
                      ? "approve"
                      : intent === "decline"
                        ? "decline"
                        : "cancel";
                  return `/business/bookings/${bookingId(form)}/${verb}`;
                }),
                suggest: decision(
                  (form) => `/business/bookings/${bookingId(form)}/suggest`,
                ),
                withdrawSuggestion: decision(
                  (form) =>
                    `/business/bookings/${bookingId(form)}/withdraw-suggestion`,
                ),
              }}
            />
          )}
        </div>
      </PageShell>
    </BusinessPage>
  );
}
